import type { AssistantAnswer, AssistantCard, AssistantSurface } from '../types/intelligence'
import { providerEarnings, providerOrders, providerPackages } from '../data/provider'
import { authorEarnings, authorTitles } from '../data/author'
import {
  accommodationPressure,
  academicActivity,
  feedbackByCategory,
  institutionSummary,
  populationByFaculty,
} from '../data/institution'
import { moderationQueue, platformMetrics, reportedAccounts, verificationQueue } from '../data/admin'
import { orderStateLabel } from '../types/marketplace'
import { naira } from './format'

/** Consoles the assistant answers separately. Anything else is not a console. */
export const OPERATOR_SURFACES = [
  'property',
  'institution',
  'provider',
  'author',
  'employer',
  'merchant',
  'representative',
  'admin',
  'operator',
] as const

export type OperatorSurface = (typeof OPERATOR_SURFACES)[number]

export function isOperatorSurface(surface: AssistantSurface): surface is OperatorSurface {
  return (OPERATOR_SURFACES as readonly string[]).includes(surface)
}

export const CONSOLE_LABEL: Record<OperatorSurface, string> = {
  property: 'the property console',
  institution: 'the institution console',
  provider: 'your provider console',
  author: 'your author console',
  employer: 'the employer console',
  merchant: 'the merchant console',
  representative: 'your representative console',
  admin: 'the Campuzon admin console',
  operator: 'this console',
}

export const CONSOLE_PROMPTS: Record<OperatorSurface, string[]> = {
  property: [
    'Which units are still vacant?',
    'What maintenance is outstanding?',
    'Which applications are waiting on me?',
  ],
  institution: [
    'How many students are active this week?',
    'Which feedback category is worst?',
    'Where is accommodation tightest?',
  ],
  provider: [
    'What are my open orders?',
    'What can I withdraw?',
    'Which package sells most?',
  ],
  author: ['What have my titles earned?', 'Which title is doing best?', 'What is pending payout?'],
  employer: [
    'Which candidates are waiting on me?',
    'Which listings are still open?',
    'How is my funnel performing?',
  ],
  merchant: [
    'What is waiting to be settled?',
    'How do I redeem a CampaGift?',
    'What are my open disputes?',
  ],
  representative: [
    'What tasks are available to me?',
    'What have I earned this month?',
    'What is blocking my activation?',
  ],
  admin: [
    'What is in the verification queue?',
    'What is waiting in moderation?',
    'How many students are on the platform?',
  ],
  operator: ['What needs my attention?', 'What is waiting on me?'],
}

function has(query: string, ...terms: string[]): boolean {
  return terms.some((term) => query.includes(term))
}

function section(id: string, title: string, meta: string, to: string): AssistantCard {
  return { id, kind: 'section', title, meta, to, provenance: 'derived' }
}

/**
 * A refusal used whenever someone asks an operator console for a student's own
 * data. The two are not the same account, and an operator holding a console does
 * not thereby hold a student's records.
 */
function crossConsoleRefusal(label: string): AssistantAnswer {
  return {
    text: 'I cannot answer that here.',
    cards: [],
    provenance: 'derived',
    refusal: {
      reason: `You are in ${label}, which holds operational records for this organisation — not a student's own applications, courses, wallet or messages.`,
      whereToLook:
        'Switch to your student console if you also hold that role. A console never reaches another account holder\u2019s records, whichever role you are using.',
    },
  }
}

/**
 * Answers a question against the console the person is actually looking at.
 *
 * The student resolver is deliberately not reachable from here: answering a
 * property manager from student housing data would be a privacy failure dressed
 * up as helpfulness.
 */
export function resolveConsole(query: string, surface: OperatorSurface): AssistantAnswer {
  // Student-scoped questions are refused in every console rather than answered
  // from whatever happens to be in scope.
  if (has(query, 'my rent', 'my course', 'my application', 'my grade', 'my timetable', 'my notes')) {
    return crossConsoleRefusal(CONSOLE_LABEL[surface])
  }

  switch (surface) {
    case 'provider':
      return resolveProvider(query)
    case 'author':
      return resolveAuthor(query)
    case 'institution':
      return resolveInstitution(query)
    case 'admin':
      return resolveAdmin(query)
    case 'property':
      return navigational(
        'the property console',
        [
          section('nav-portfolio', 'Portfolio and occupancy', 'Units, beds and vacancy', '/property'),
          section('nav-apps', 'Applications', 'Applicants waiting on a decision', '/property'),
          section('nav-rent', 'Rent ledger', 'Charges, receipts and arrears', '/property'),
          section('nav-maint', 'Maintenance', 'Open and closed requests', '/property'),
        ],
        'occupancy, applications, tenants, rent and maintenance for your properties',
      )
    case 'employer':
      return navigational(
        'the employer console',
        [
          section('nav-pipeline', 'Candidate pipeline', 'Applied through to hired', '/employer'),
          section('nav-listings', 'Listings', 'Draft, published and closed roles', '/employer'),
          section('nav-analytics', 'Funnel and sources', 'Aggregate only', '/employer'),
        ],
        'your listings, candidates and hiring funnel',
      )
    case 'merchant':
      return navigational(
        'the merchant console',
        [
          section('nav-desk', 'Redemption desk', 'Look a gift up and redeem', '/merchant'),
          section('nav-settle', 'Settlements', 'What is owed and when', '/merchant'),
          section('nav-disputes', 'Disputes', 'Raised against your redemptions', '/merchant'),
        ],
        'CampaGift redemption, settlement and disputes',
      )
    case 'representative':
      return navigational(
        'your representative console',
        [
          section('nav-tasks', 'Tasks', 'What your rank and training unlock', '/representative'),
          section('nav-earn', 'Earnings', 'Pending, approved, paid', '/representative'),
          section('nav-train', 'Training', 'Assessments that unlock task types', '/representative'),
        ],
        'your tasks, evidence, training and earnings',
      )
    default:
      return navigational(
        'this console',
        [],
        'operational records for the organisation whose console you are holding',
      )
  }
}

function navigational(label: string, cards: AssistantCard[], scope: string): AssistantAnswer {
  return {
    text: `You are in ${label}. I can point you at ${scope} — I do not summarise records I have not been given a source for.`,
    cards,
    provenance: 'derived',
  }
}

/* ------------------------------------------------------------------ provider */

function resolveProvider(query: string): AssistantAnswer {
  if (has(query, 'earn', 'withdraw', 'payout', 'balance', 'money')) {
    return {
      text: `Withdrawable ${naira(providerEarnings.withdrawableNaira)}. Escrow holds ${naira(
        providerEarnings.escrowNaira,
      )} against work in progress, and ${naira(
        providerEarnings.pendingNaira,
      )} is inside the ${providerEarnings.refundWindowDays}-day refund window.`,
      cards: [
        {
          id: 'prov-earn',
          kind: 'balance',
          title: 'Earnings',
          meta: `Available ${naira(providerEarnings.availableNaira)} · fee ${Math.round(
            providerEarnings.feeRate * 100,
          )}%`,
          detail: 'Balances are derived from the ledger, never stored as a figure.',
          to: '/provider',
          provenance: 'derived',
        },
      ],
      provenance: 'derived',
    }
  }

  if (has(query, 'package', 'sell', 'offer', 'service')) {
    const ranked = [...providerPackages].sort((a, b) => b.ordersThisMonth - a.ordersThisMonth)
    return {
      text: `${ranked[0]?.name} has taken the most orders this month.`,
      cards: ranked.slice(0, 3).map((pack) => ({
        id: pack.id,
        kind: 'section' as const,
        title: pack.name,
        meta: `${naira(pack.priceNaira)} · ${pack.ordersThisMonth} orders this month`,
        detail: pack.active ? undefined : 'Not currently offered to buyers.',
        to: '/provider',
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  const open = providerOrders.filter(
    (order) => order.state !== 'completed' && order.state !== 'cancelled',
  )
  return {
    text:
      open.length === 0
        ? 'Nothing is open. Every order you have accepted has completed or been cancelled.'
        : `${open.length} order${open.length === 1 ? '' : 's'} still need something from you.`,
    cards: open.slice(0, 4).map((order) => ({
      id: order.id,
      kind: 'order' as const,
      title: order.title,
      meta: `${order.reference} · ${naira(order.amountNaira)} · ${order.dueLabel}`,
      detail: `Buyer ${order.counterparty}`,
      badge: { label: orderStateLabel(order.state), tone: 'brand' as const },
      to: '/provider',
      provenance: 'verified' as const,
    })),
    provenance: 'derived',
  }
}

/* -------------------------------------------------------------------- author */

function resolveAuthor(query: string): AssistantAnswer {
  if (has(query, 'best', 'top', 'doing', 'popular', 'title')) {
    const ranked = [...authorTitles].sort((a, b) => b.earningsNaira - a.earningsNaira)
    return {
      text: `${ranked[0]?.title} has earned the most, at ${naira(ranked[0]?.earningsNaira ?? 0)}.`,
      cards: ranked.slice(0, 3).map((title) => ({
        id: title.id,
        kind: 'resource' as const,
        title: title.title,
        meta: `${naira(title.earningsNaira)} · ${title.purchases} purchases · ${title.rentals} rentals`,
        detail: `${title.activeEntitlements} entitlements are currently active.`,
        badge: { label: title.status, tone: 'neutral' as const },
        to: '/author',
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  return {
    text: `Gross ${naira(authorEarnings.grossNaira)} less ${naira(
      authorEarnings.platformFeeNaira,
    )} platform fee leaves ${naira(authorEarnings.netNaira)}. ${naira(
      authorEarnings.pendingPayoutNaira,
    )} is awaiting payout.`,
    cards: [
      {
        id: 'author-earn',
        kind: 'balance',
        title: 'Earnings and payouts',
        meta: 'Derived from entitlement records',
        detail: 'Payout execution sits with a licensed provider, not with Campuzon.',
        to: '/author',
        provenance: 'derived',
      },
    ],
    provenance: 'derived',
  }
}

/* --------------------------------------------------------------- institution */

function resolveInstitution(query: string): AssistantAnswer {
  if (has(query, 'accommodation', 'housing', 'rent', 'tight')) {
    const worst = [...accommodationPressure][0]
    return {
      text: `${worst?.neighbourhood} is under the most pressure — ${worst?.demand} demand against ${worst?.listedBeds} listed beds.`,
      cards: accommodationPressure.slice(0, 3).map((row) => ({
        id: row.neighbourhood,
        kind: 'metric' as const,
        title: row.neighbourhood,
        meta: `${row.listedBeds} beds listed · median ${row.medianRent}`,
        detail: `Demand: ${row.demand}`,
        to: '/institution',
        provenance: 'derived' as const,
      })),
      provenance: 'derived',
    }
  }

  if (has(query, 'feedback', 'complaint', 'issue', 'worst')) {
    const ranked = [...feedbackByCategory].sort((a, b) => b.open - a.open)
    return {
      text: `${ranked[0]?.category} has the most open feedback — ${ranked[0]?.open} items, median ${ranked[0]?.medianDays} days to resolve.`,
      cards: ranked.slice(0, 3).map((row) => ({
        id: row.category,
        kind: 'metric' as const,
        title: row.category,
        meta: `${row.open} open · ${row.resolved} resolved`,
        detail: `Median ${row.medianDays} days to resolution.`,
        to: '/institution',
        provenance: 'derived' as const,
      })),
      provenance: 'derived',
    }
  }

  if (has(query, 'faculty', 'department', 'engaged', 'population')) {
    return {
      text: `${institutionSummary.students.toLocaleString()} students across the faculties Campuzon can see.`,
      cards: populationByFaculty.slice(0, 4).map((row) => ({
        id: row.faculty,
        kind: 'metric' as const,
        title: row.faculty,
        meta: `${row.students.toLocaleString()} students · ${row.engaged.toLocaleString()} engaged`,
        to: '/institution',
        provenance: 'derived' as const,
      })),
      provenance: 'derived',
    }
  }

  return {
    text: `${institutionSummary.activeThisWeek.toLocaleString()} of ${institutionSummary.students.toLocaleString()} students were active this week. Everything here is aggregate — no individual student is identifiable in these figures.`,
    cards: academicActivity.slice(0, 3).map((row) => ({
      id: row.label,
      kind: 'metric' as const,
      title: row.label,
      meta: `${row.value} · ${row.delta}`,
      to: '/institution',
      provenance: 'derived' as const,
    })),
    provenance: 'derived',
  }
}

/* --------------------------------------------------------------------- admin */

function resolveAdmin(query: string): AssistantAnswer {
  if (has(query, 'verif')) {
    const open = verificationQueue.filter((item) => item.status !== 'Approved')
    return {
      text: `${open.length} verification task${open.length === 1 ? '' : 's'} are open.`,
      cards: open.slice(0, 4).map((item) => ({
        id: item.id,
        kind: 'queue' as const,
        title: item.subject,
        meta: `${item.type} · submitted ${item.submitted}`,
        detail: `Raised by ${item.submittedBy}`,
        badge: { label: item.status, tone: 'warn' as const },
        to: '/admin/verification',
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  if (has(query, 'moderat', 'report', 'abuse', 'flag')) {
    return {
      text: `${moderationQueue.length} content items and ${reportedAccounts.length} accounts are in moderation. A queued item is a report, not a finding.`,
      cards: reportedAccounts.slice(0, 3).map((account) => ({
        id: account.id,
        kind: 'queue' as const,
        title: account.account,
        meta: `${account.role} · ${account.reports} reports`,
        badge: { label: account.state, tone: 'warn' as const },
        to: '/admin/users',
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  return {
    text: 'Platform figures, all aggregate.',
    cards: platformMetrics.slice(0, 4).map((metric, index) => ({
      id: `metric-${index}`,
      kind: 'metric' as const,
      title: metric.label,
      meta: `${metric.value}${metric.delta ? ` · ${metric.delta}` : ''}`,
      to: '/admin',
      provenance: 'derived' as const,
    })),
    provenance: 'derived',
  }
}
